import React, { memo } from 'react'

import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

import { TableSorter, TableColumnConfig, TableSorterDirection } from './types'
import { Icon } from '@/Atomic/Atoms/Icon'
import { StringText } from '@/Atomic/Atoms/StringText'
import { TableHeader as TableHeaderElement } from '@/Atomic/Templates/Table'

interface TableHeaderProps extends TableColumnConfig {
  sorter?: TableSorter
  sortColumn?: (accessor: string) => void
  isReorderingColumnsActive: boolean
  selectAllRows?: (select?: boolean) => void
  isAllRowsSelected?: () => boolean | undefined
  isAllRowsSelectDisabled?: () => boolean
}

export const TableHeader: React.FC<TableHeaderProps> = memo(
  ({
    accessor,
    title,
    variant,
    sortable,
    sorter,
    sortColumn,
    isReorderingColumnsActive,
    selectAllRows,
    isAllRowsSelected,
    isAllRowsSelectDisabled,
  }) => {
    const {
      attributes,
      listeners,
      setNodeRef: _setNodeRef,
      transform,
      transition,
      setDraggableNodeRef,
    } = useSortable({ id: accessor })

    const isSortingActive = sorter?.accessor === accessor

    const onClick = () => {
      if (sortable) {
        if (sortColumn) {
          sortColumn(accessor)
        }
      }
    }

    const style: React.CSSProperties = {
      transform: CSS.Translate.toString(transform),
      transition,
    }

    return (
      <TableHeaderElement
        // ref={setNodeRef}
        style={style}
        onClick={onClick}
        key={accessor}
        text={title}
        variant={variant}
        sortable={sortable}
        sortDirection={isSortingActive ? sorter?.direction : undefined}
        selectAllRows={selectAllRows}
        isAllRowsSelected={isAllRowsSelected}
        isAllRowsSelectDisabled={isAllRowsSelectDisabled}
      >
        {isReorderingColumnsActive && (
          <i
            ref={setDraggableNodeRef}
            className="svg-icon icon-more-horizontal icon-sm"
            title="Drag to reorder columns"
            {...attributes}
            {...listeners}
          />
        )}

        <StringText value={title} />

        {isSortingActive && sorter?.direction === TableSorterDirection.ASC && (
          <Icon icon="chevron-up" size="sm" />
        )}
        {isSortingActive && sorter?.direction === TableSorterDirection.DESC && (
          <Icon icon="chevron-down" size="sm" />
        )}
        {sortable && !isSortingActive && (
          <Icon icon="chevron-up" size="sm" className="sort-placeholder" />
        )}
      </TableHeaderElement>
    )
  },
)

TableHeader.displayName = 'TableHeader'
